/**
 * Delivery event contracts — slice 6, "results & credits".
 *
 * Slice 5 could send but learned nothing afterwards: every number a campaign
 * screen showed stopped at "handed to SES", and **accepted is not delivered**.
 * This file is the vocabulary of what a provider tells us afterwards, and the
 * one place that says what each of those things means.
 *
 * Three rules shape it.
 *
 * **1. This is the file that parses hostile input.** Everything here is derived
 * from an HTTP request anyone on the internet can make. `credit.ts` — the ledger
 * — parses nothing off a wire we do not own, and the only arrow between the two
 * files is {@link CREDIT_EFFECT_BY_EVENT}, a nine-row table imported from there
 * as {@link import('./credit.js').CreditEffect}. That is deliberate: a reviewer
 * asking "what can a forged payload move" reads one table, not a module.
 *
 * **Verify first, parse second, act third.** {@link isTrustedSnsSigningCertUrl}
 * is the security-critical line in the slice — the signing-certificate URL is
 * checked to be an AWS HTTPS host **before any request is made to it**, or the
 * signature verifier becomes an SSRF that fetches whatever an attacker names.
 * It lives here, next to the event shapes, for the same reason
 * `evaluateSendGuard` lives in `campaign.ts`: one implementation, failing
 * closed, callable by the endpoint and by a test.
 *
 * **2. One table decides what an event means.** {@link deliveryOutcome} returns
 * the credit movement, the suppression, and the two statuses together, because
 * they are one decision taken from one payload. SLICES: *a delivery consumes one
 * credit; a hard bounce releases the reservation and writes a suppression;
 * neither ever does the other's job.* A `switch` in the ingest service and
 * another in the dashboard is exactly how the ledger and the screen come to
 * disagree about whether a complaint consumed a credit.
 *
 * **3. Hard and soft bounces are not the same event.** A hard bounce suppresses
 * permanently; a soft bounce must not. Getting it backwards either burns a
 * list — a real customer never mailed again — or keeps mailing an address that
 * will never accept, which is how a tenant's own AWS account gets suspended
 * under BYO. {@link isHardBounce} is the single rule, and it reads
 * `bounceType`, never the subtype: `General` appears under both `Permanent` and
 * `Transient`, so a rule keyed on the subtype suppresses full mailboxes.
 */
import { z } from 'zod';

import { projectedFiguresSchema } from './projection.js';

import { isoDateTimeSchema, paginated, paginationQuerySchema, uuidSchema } from '../common.js';
import {
  integrationKindSchema,
  messageStatusSchema,
  type Channel,
  type MessageStatus,
  type RecipientStatus,
  type SuppressionReason,
} from '../enums.js';
import {
  campaignCreditTotalsSchema,
  creditsStillReserved,
  type CampaignCreditTotals,
  type CreditEffect,
} from './credit.js';
import { recipientStatusCountsSchema, type RecipientStatusCounts } from './campaign.js';

/* -------------------------------------------------------------------------- */
/* Wire values — one normalisation, used four times                            */
/* -------------------------------------------------------------------------- */

/**
 * Folds a provider's spelling of an enum member down to something matchable.
 *
 * It exists because of one AWS detail that is easy to miss and expensive to get
 * wrong: SES's event-publishing payload spells the rendering-failure event
 * **`"Rendering Failure"`, with a space**, while the SESv2 API's own enum calls
 * it `RENDERING_FAILURE` and the SDK calls it `RenderingFailure`. Three
 * spellings of one thing, and an exact-match table would silently fail to
 * recognise two of them — silently, because an unrecognised event is stored and
 * not acted on, which looks exactly like an event that never arrived.
 *
 * Folding case and non-alphanumerics is safe here and was checked rather than
 * assumed: no two members of any of the four vocabularies below collide under
 * it (`delivery` and `deliverydelay` stay distinct).
 */
function foldWireValue(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Builds a lenient parser from a table of documented wire spellings.
 *
 * Returns `null` rather than throwing for an unknown value, and every caller
 * treats `null` as "store the raw payload, act on nothing". AWS adds event types
 * — `DeliveryDelay` and `Subscription` both arrived after SES event publishing
 * shipped — and a parser that threw would turn a new event type into a 500 and a
 * retry storm from SNS.
 */
function wireParser<T extends string>(table: Readonly<Record<string, T>>): (raw: unknown) => T | null {
  const folded = new Map<string, T>();
  for (const [wire, member] of Object.entries(table)) folded.set(foldWireValue(wire), member);
  return (raw: unknown) => (typeof raw === 'string' ? (folded.get(foldWireValue(raw)) ?? null) : null);
}

/* -------------------------------------------------------------------------- */
/* Which channels delivery is measured on at all                               */
/* -------------------------------------------------------------------------- */

/**
 * The channels a `delivery_events` row can exist for. **Both of them, since
 * slice 12.**
 *
 * This said "email, and only email" from slice 6 to slice 11, and the paragraph
 * it said it in is kept below rather than deleted, because none of it was wrong:
 * `delivery_events` was written by exactly one ingest — the SNS controller in
 * `webhooks` — so a WhatsApp message had nothing behind it and its
 * `messages.status` stayed `sent` for ever, however well the send actually went.
 * The note that used to close this comment reserved the edit for *"the slice
 * that ships the status webhook, and only once it is actually inserting rows —
 * not when the route exists"*, and slice 12 is that slice: Meta's message-status
 * ingest writes `sent` / `delivered` / `read` / `failed` rows against
 * `provider = 'meta_whatsapp'`, so a delivered WhatsApp message is now a fact in
 * this database rather than an assumption.
 *
 * **This is a constant rather than a comment because the difference cannot be
 * seen in the counts.** "Nobody received it" and "this channel is not measured"
 * are both `0`, and a screen that prints the number renders the first when the
 * truth is the second — the same failure the engagement panel refuses by having
 * no field a number can live in when tracking is off (`engagementPanelSchema`
 * in `./engagement.js`), reached through the delivered column instead. Every
 * read that would report a delivered figure has to branch on this and say the
 * sentence instead of showing the zero.
 *
 * **It is now two predicates and not one**, and the split is the point. Slice 10
 * had one question to ask because email answers both halves the same way. Meta
 * answers them differently: it reports delivery, and it has no such thing as a
 * bounce. See {@link BOUNCE_MEASURED_CHANNELS}.
 */
export const DELIVERY_MEASURED_CHANNELS: readonly Channel[] = ['email', 'whatsapp'];

/**
 * Whether a **delivered** count on this channel is a measurement at all.
 *
 * The callers are the two screens that show that count — the Overview's
 * recent-campaign rows and the campaign results panel — and both use it to
 * choose between a number and a sentence, never to hide the row.
 */
export function isDeliveryMeasured(channel: Channel): boolean {
  return DELIVERY_MEASURED_CHANNELS.includes(channel);
}

/**
 * The channels on which **"bounced" is a category that exists**. Email, and only
 * email — and unlike {@link DELIVERY_MEASURED_CHANNELS}, this one is not waiting
 * for a later slice to widen it.
 *
 * Slice 12, decision 1: **there is no bounced on WhatsApp.** Meta reports
 * `sent`, `delivered`, `read`, `failed` and `deleted`, and a `failed` carries an
 * error code that is the entire diagnosis — an unregistered number, a policy
 * block, a rate limit and an expired 24-hour window all arrive as one word and
 * mean four different things. `WA_FAILURE_CLASS_BY_CODE` in
 * `./whatsapp-delivery.js` splits them seven ways, and none of the seven is
 * called a bounce.
 *
 * So a WhatsApp row's `bounced` stays `null` — permanently, not pending — and a
 * screen can never print `Bounced 0` beside a WhatsApp send. That zero would be
 * arithmetic about a category the channel does not have, which is a subtler lie
 * than the one slice 10 caught: it is not "we did not look", it is "we counted
 * something that cannot happen".
 *
 * This also keeps the SES-shaped reads structurally honest.
 * {@link notDeliveredBreakdownSchema}'s five outcomes and
 * {@link bounceBreakdownSchema}'s three are AWS's vocabulary end to end —
 * `reject`, `rendering_failure`, `Permanent`/`Transient` — and are gated by this
 * predicate rather than by {@link isDeliveryMeasured}, because four of the eight
 * members are bounce or SES-refusal events with no WhatsApp counterpart at all.
 * `waFailureBreakdown` is the equivalent read on the other channel.
 */
export const BOUNCE_MEASURED_CHANNELS: readonly Channel[] = ['email'];

/**
 * Whether "bounced" means anything on this channel. See
 * {@link BOUNCE_MEASURED_CHANNELS} — on WhatsApp it does not, and the answer is
 * not going to change.
 */
export function isBounceMeasured(channel: Channel): boolean {
  return BOUNCE_MEASURED_CHANNELS.includes(channel);
}

/* -------------------------------------------------------------------------- */
/* The SES event types we ingest                                               */
/* -------------------------------------------------------------------------- */

/**
 * Every event type an SES event destination can publish, in Wend's vocabulary.
 *
 * The set is closed and it is **AWS's, not ours** — carrying only the five that
 * matter for credits would mean the other five arrive, fail to parse, and are
 * indistinguishable from an event that never came. They cost a row in a table
 * each, and each row is a decision somebody made on purpose.
 *
 * Written in `snake_case` rather than as AWS's `"Rendering Failure"` because
 * this value goes into `delivery_events.type` and is read back for years: a
 * column holding a string with a space in it, in a codebase whose every other
 * enum is lower snake_case, is a column that gets compared with the wrong
 * literal eventually. {@link parseSesEventType} owns the translation, and
 * {@link SES_EVENT_TYPE_BY_WIRE_VALUE} is the only place AWS's spellings appear.
 *
 * Like slice 5's skip reasons, this is not one of CONTRACT.md's fixed enums, so
 * it lives beside the table that gives it meaning rather than in `enums.ts` —
 * splitting the members from {@link CREDIT_EFFECT_BY_EVENT} is how a member gets
 * added without anybody deciding what it does to a credit. If `@wend/db` mirrors
 * it as a pg enum it must import {@link SES_EVENT_TYPE_VALUES} rather than
 * re-typing the members, exactly as the fixed enums are mirrored.
 *
 * **`open` and `click` are opt-in per tenant, and they are off by default.**
 * This paragraph used to say they *must not be switched on*, and slice 11
 * reversed that on purpose. The reasoning it was built on is not being deleted,
 * because none of it turned out to be wrong — what changed is who gets to
 * decide.
 *
 * The old position: SES implements open tracking by injecting a 1×1 pixel into
 * the HTML and click tracking by rewriting every link through an AWS redirect.
 * Both destroy the property slice 4 established when it removed MJML's Google
 * Fonts link and slice 5 kept when it embedded images as CID parts — *an email
 * Wend sends makes no network request when it is opened* — so the enum carried
 * the two members merely to understand an event somebody had enabled by hand in
 * the AWS console, and never to invite one.
 *
 * What that got wrong was treating a property of the *product* as a property of
 * every tenant's mail. Some tenants need to promise their members no tracking
 * and must be able to keep that promise; others are sending campaigns whose
 * whole point is to learn whether anybody read them, and telling them "no"
 * bought a guarantee they had not asked for at a price they were paying. So the
 * decision moved to the tenant, and the sentence became conditional rather than
 * false:
 *
 *   **An email Wend sends makes no network request when it is opened, unless
 *   that tenant has switched tracking on — and with tracking on it carries a
 *   1×1 pixel, and every link in it is rewritten through a redirect on a domain
 *   that tenant owns.**
 *
 * Slice 21 added a second "unless", and it belongs to the deployment rather than
 * the tenant: **or the email has pictures and this deployment hosts them**
 * (`TEMPLATE_ASSET_PUBLIC_BASE_URL` set). A hosted picture is fetched from Wend's
 * image host when the message is opened, tracking or no tracking. See
 * `trackingOffDisclosure` in `./engagement.js`.
 *
 * Both halves are enforced, not merely stated. Tracking is off in
 * `TRACKING_OFF`, the default every tenant starts at; and **with tracking off
 * the `SendEmailCommand` names no configuration set that can modify a
 * message**, which is what makes the guarantee real — SES injects nothing into a
 * message sent through a set with no destination matching OPEN or CLICK and no
 * VDM engagement tracking, or through no set at all, so there is no pixel to
 * suppress and no link to un-rewrite. (Account-wide engagement tracking is the
 * exception no set name reaches; the API's `SES_SEND_TRACKING_OVERRIDES` is the
 * answer to it.) Since slice 19 that send names the delivery reports set when one
 * is set up, and nothing otherwise. `configurationSetNameForSend` in
 * `./engagement.js` is the single place the name can come from, and the
 * assertion belongs on the command object: Wend's raw MIME is byte-identical
 * either way, because SES does the injecting server-side after we hand the
 * message over, so a MIME comparison is a test that cannot fail.
 *
 * The two members' place in this file is otherwise unchanged. They still move no
 * credit ({@link CREDIT_EFFECT_BY_EVENT}) and still suppress nobody
 * ({@link deliveryOutcome}): an open is not a delivery and a click is not
 * consent, and reading either as one would be a new bug and not a new feature.
 */
export const SES_EVENT_TYPE_VALUES = [
  'send',
  'delivery',
  'bounce',
  'complaint',
  'reject',
  'rendering_failure',
  'delivery_delay',
  'subscription',
  'open',
  'click',
] as const;
export const sesEventTypeSchema = z.enum(SES_EVENT_TYPE_VALUES);
export type SesEventType = z.infer<typeof sesEventTypeSchema>;

/** AWS's documented spellings. The only place they appear. */
export const SES_EVENT_TYPE_BY_WIRE_VALUE: Readonly<Record<string, SesEventType>> = {
  Send: 'send',
  Delivery: 'delivery',
  Bounce: 'bounce',
  Complaint: 'complaint',
  Reject: 'reject',
  // The space is not a typo — see foldWireValue.
  'Rendering Failure': 'rendering_failure',
  DeliveryDelay: 'delivery_delay',
  Subscription: 'subscription',
  Open: 'open',
  Click: 'click',
};

export const parseSesEventType = wireParser(SES_EVENT_TYPE_BY_WIRE_VALUE);

/* -------------------------------------------------------------------------- */
/* Bounce classification — the one that burns a list if it is backwards        */
/* -------------------------------------------------------------------------- */

/**
 * SES's `bounceType`. **This, and only this, decides suppression.**
 *
 *   - `permanent` — the address does not exist or has refused permanently. It
 *     will never accept a message, and continuing to mail it is what drives an
 *     account's bounce rate towards suspension;
 *   - `transient` — it did not arrive *this time*. SES has already retried for
 *     its own retry window and given up; the address itself may be perfectly
 *     good, and a full mailbox is empty again next week;
 *   - `undetermined` — SES could not classify it. Treated as transient for
 *     suppression, which is the asymmetric choice: see {@link isHardBounce}.
 */
export const SES_BOUNCE_TYPE_VALUES = ['permanent', 'transient', 'undetermined'] as const;
export const sesBounceTypeSchema = z.enum(SES_BOUNCE_TYPE_VALUES);
export type SesBounceType = z.infer<typeof sesBounceTypeSchema>;

export const SES_BOUNCE_TYPE_BY_WIRE_VALUE: Readonly<Record<string, SesBounceType>> = {
  Permanent: 'permanent',
  Transient: 'transient',
  Undetermined: 'undetermined',
};
export const parseSesBounceType = wireParser(SES_BOUNCE_TYPE_BY_WIRE_VALUE);

/**
 * SES's `bounceSubType`. Carried for the diagnosis; it decides **nothing**.
 *
 * That is worth stating rather than leaving to be inferred, because the subtype
 * is the more descriptive field and it is the one a reader reaches for. Two of
 * these members appear under more than one `bounceType`:
 *
 *   - `general` is what most `Permanent` bounces carry **and** what most
 *     `Transient` ones do. A rule that suppressed on `general` would suppress
 *     every temporarily-full mailbox in the list;
 *   - `suppressed` and `on_account_suppression_list` mean the address was
 *     already on **SES's own account-level suppression list** from an earlier
 *     hard bounce. They are `Permanent`, and treating them as anything else
 *     would mail an address AWS has already told us about twice.
 *
 * `message_too_large`, `content_rejected` and `attachment_rejected` are the
 * clearest argument for the whole split: they are bounces about **our message**,
 * not about the recipient. Suppressing on one of those would permanently stop
 * mailing a real customer because we once sent them a PDF that was too big.
 */
export const SES_BOUNCE_SUBTYPE_VALUES = [
  'general',
  'no_email',
  'suppressed',
  'on_account_suppression_list',
  'mailbox_full',
  'message_too_large',
  'content_rejected',
  'attachment_rejected',
  'undetermined',
] as const;
export const sesBounceSubTypeSchema = z.enum(SES_BOUNCE_SUBTYPE_VALUES);
export type SesBounceSubType = z.infer<typeof sesBounceSubTypeSchema>;

export const SES_BOUNCE_SUBTYPE_BY_WIRE_VALUE: Readonly<Record<string, SesBounceSubType>> = {
  General: 'general',
  NoEmail: 'no_email',
  Suppressed: 'suppressed',
  OnAccountSuppressionList: 'on_account_suppression_list',
  MailboxFull: 'mailbox_full',
  MessageTooLarge: 'message_too_large',
  ContentRejected: 'content_rejected',
  AttachmentRejected: 'attachment_rejected',
  Undetermined: 'undetermined',
};
export const parseSesBounceSubType = wireParser(SES_BOUNCE_SUBTYPE_BY_WIRE_VALUE);

/**
 * **The hard/soft rule.** A hard bounce suppresses permanently; nothing else
 * does.
 *
 * `null` — a bounce whose type we could not read — is **not** hard, and the
 * asymmetry is the whole reason this function takes a nullable argument rather
 * than defaulting somewhere upstream. The two ways to be wrong are not equally
 * bad:
 *
 *   - failing to suppress a dead address costs reputation, is visible in the
 *     bounce rate, and is repaired by the next bounce from the same address.
 *     SES also adds hard-bounced addresses to its own account-level suppression
 *     list, so AWS is already protecting the account while we are wrong;
 *   - suppressing a live address permanently is not repaired by anything. The
 *     row is append-only by design, `suppressions` grants the application role
 *     neither UPDATE nor DELETE, and the customer is a bank that stops hearing
 *     from its own summit organiser and never finds out why.
 *
 * So an unparseable bounce releases its credit (it did not arrive) and
 * suppresses nobody. The raw payload is in `webhook_events` either way, which is
 * what makes "act on less than we stored" a safe default rather than a loss.
 *
 * An address that bounces `undetermined` repeatedly deserves suppressing. That
 * is a repeat-bounce rule over `delivery_events`, it needs history rather than
 * one payload, and it is not this slice.
 */
export function isHardBounce(bounceType: SesBounceType | null): boolean {
  return bounceType === 'permanent';
}

/* -------------------------------------------------------------------------- */
/* Complaint feedback                                                          */
/* -------------------------------------------------------------------------- */

/**
 * ARF feedback types, as SES passes them through in `complaintFeedbackType`.
 *
 * `not_spam` is why this is parsed at all rather than treated as one kind of
 * event. It is a **withdrawal** — the recipient, or their provider, moved the
 * message back out of the spam folder — and suppressing on it would permanently
 * stop mailing somebody at the exact moment they said the opposite. It is rare,
 * and rare is what makes it worth handling here instead of discovering it.
 *
 * The field is frequently **absent**: many providers report a complaint without
 * a type. Absent is not `not_spam`, so a missing type suppresses.
 */
export const SES_COMPLAINT_FEEDBACK_TYPE_VALUES = [
  'abuse',
  'auth_failure',
  'fraud',
  'not_spam',
  'other',
  'virus',
] as const;
export const sesComplaintFeedbackTypeSchema = z.enum(SES_COMPLAINT_FEEDBACK_TYPE_VALUES);
export type SesComplaintFeedbackType = z.infer<typeof sesComplaintFeedbackTypeSchema>;

export const SES_COMPLAINT_FEEDBACK_TYPE_BY_WIRE_VALUE: Readonly<
  Record<string, SesComplaintFeedbackType>
> = {
  abuse: 'abuse',
  'auth-failure': 'auth_failure',
  fraud: 'fraud',
  'not-spam': 'not_spam',
  other: 'other',
  virus: 'virus',
};
export const parseSesComplaintFeedbackType = wireParser(SES_COMPLAINT_FEEDBACK_TYPE_BY_WIRE_VALUE);

/* -------------------------------------------------------------------------- */
/* THE MAPPING — what an event means for credits                               */
/* -------------------------------------------------------------------------- */

/**
 * **What each event does to the ledger.** Total over {@link SesEventType}, so a
 * new event type cannot be added without deciding this.
 *
 * ```
 *   send               null      the reservation already exists — see below
 *   delivery           consume   ← the only one
 *   bounce             release   it did not arrive; the reservation unwinds
 *   reject             release   SES refused it outright; it never left
 *   rendering_failure  release   never rendered, never sent
 *   complaint          null      ← the interesting one
 *   delivery_delay     null      still in flight; the reservation stays
 *   subscription       null
 *   open · click       null
 * ```
 *
 * **WHY A BOUNCE NEVER CONSUMES A CREDIT.** There is no rule here that says so,
 * and there must not be one. Look at the table: `consume` appears exactly once,
 * against `delivery`. A credit is consumed by a message *arriving*, so an event
 * whose entire content is that a message did not arrive has nothing to consume
 * with — not because consuming is forbidden to it, but because consumption is
 * not something it can express. {@link CONSUMING_EVENT_TYPES} is derived from
 * this table rather than written out, so "exactly one thing consumes" is a fact
 * about the model rather than an assertion somebody has to keep true.
 *
 * **WHY A COMPLAINT DOES NOT RELEASE ONE EITHER**, which is the same argument
 * read backwards and the one most likely to be got wrong. A complaint arrives
 * *after* a delivery: the message reached the recipient's inbox and they marked
 * it as spam from there. The delivery consumed the credit and the delivery
 * really happened, so there is nothing to give back — releasing here would hand
 * back a credit that was legitimately used, and would try to decrement a
 * `reserved` the delivery had already taken to zero, aborting a transaction that
 * should have been a no-op. A complaint's consequence is a permanent
 * suppression, which is a compliance action and not a credit one.
 *
 * **`send` is `null` for a reason worth stating.** SES's own `Send` event says
 * the provider accepted the message — but the reservation was written in the
 * same transaction as the `messages` row, before this event could exist, so
 * there is nothing left for it to do to the ledger. What it *does* do is carry
 * the provider id that closes slice 5's deliberate gap between "provider
 * accepted" and "we recorded it": see {@link deliveryOutcome}, which moves a
 * `unknown` message to `sent`.
 *
 * **`delivery_delay` is `null`, and releasing there would be a real bug.** SES
 * is still retrying. Releasing the reservation and then delivering would consume
 * a credit that nothing had reserved, which `credit_balances.reserved >= 0`
 * turns into a failed transaction on a message that succeeded.
 *
 * **`open` and `click` are `null`, and slice 11 switching them on changes
 * nothing here.** They now actually arrive for tenants who opted in, which makes
 * this row worth stating rather than assuming: a credit is consumed by a message
 * *arriving*, and the delivery that preceded the open already consumed it. A
 * `consume` here would double-charge every tracked message that anybody read,
 * and would take a `reserved` the delivery had already emptied. They are also
 * `null` in the other direction — an open is not consent and a click is not
 * consent, so neither may ever produce a suppression or its reverse; see
 * {@link deliveryOutcome}.
 */
export const CREDIT_EFFECT_BY_EVENT: Readonly<Record<SesEventType, CreditEffect>> = {
  send: null,
  delivery: 'consume',
  bounce: 'release',
  complaint: null,
  reject: 'release',
  rendering_failure: 'release',
  delivery_delay: null,
  subscription: null,
  open: null,
  click: null,
};

/**
 * The events that consume a credit. **Derived, never written out.**
 *
 * It has one member, and it has one member as a consequence of the table above
 * rather than as a claim made next to it. The dashboard uses it to justify the
 * sentence it prints beside the consumed figure; the unit tests assert its
 * length, which is the cheapest possible guard on the most expensive possible
 * mistake.
 */
export const CONSUMING_EVENT_TYPES: readonly SesEventType[] = SES_EVENT_TYPE_VALUES.filter(
  (type) => CREDIT_EFFECT_BY_EVENT[type] === 'consume',
);

/** The credit movement this event type causes. A lookup, never a `switch`. */
export function creditEffectForEvent(type: SesEventType): CreditEffect {
  return CREDIT_EFFECT_BY_EVENT[type];
}

/* -------------------------------------------------------------------------- */
/* The outcome — credits, suppression and two statuses, from one payload       */
/* -------------------------------------------------------------------------- */

/** The fields of a parsed event that any decision here is allowed to read. */
export interface DeliveryEventFacts {
  readonly type: SesEventType;
  /** Present on a `bounce`. Either half may be null when SES omitted it. */
  readonly bounce?: {
    readonly type: SesBounceType | null;
    readonly subType: SesBounceSubType | null;
  } | null;
  /** Present on a `complaint`. `feedbackType` is absent more often than not. */
  readonly complaint?: { readonly feedbackType: SesComplaintFeedbackType | null } | null;
}

/**
 * Everything one event changes. Nothing here is optional guidance — the ingest
 * transaction applies all four fields or none of them.
 */
export interface DeliveryOutcome {
  /** The ledger entry to write, or `null`. See {@link CREDIT_EFFECT_BY_EVENT}. */
  readonly credit: CreditEffect;
  /** The suppression to write, or `null`. Permanent when present. */
  readonly suppression: SuppressionReason | null;
  /** What `messages.status` becomes. `null` leaves it alone. */
  readonly messageStatus: MessageStatus | null;
  /** What `campaign_recipients.status` becomes. `null` leaves it alone. */
  readonly recipientStatus: RecipientStatus | null;
}

/**
 * **The one place an event becomes a decision.**
 *
 * Credits, suppression and the two statuses come back together because they are
 * one reading of one payload, and separating them is how they come to disagree:
 * a service that consults a credit table here and a suppression `switch` there
 * will eventually suppress on a soft bounce in one of them.
 *
 * Note what a **complaint** returns — `credit: null`, `messageStatus: null`, and
 * a permanent suppression. The message stays `delivered`, because it was: a
 * complaint is something the recipient did with a message that arrived. There is
 * no `complained` member in `MessageStatus` or `RecipientStatus` and there must
 * not be one; adding it would overwrite the delivery, and with it the record of
 * why that message's credit is legitimately consumed. Complaints are counted
 * separately — see {@link campaignResultsSchema}.
 *
 * Note also that the **bounce subtype never reaches the credit decision**. Both
 * kinds of bounce release, always; the subtype's only reader is
 * {@link isHardBounce}'s input, `bounceType`. So the worst a misclassified
 * bounce can do is suppress wrongly or fail to — it cannot corrupt the ledger.
 * That separation is deliberate, and it is why the two questions are answered by
 * two different fields of the payload.
 */
export function deliveryOutcome(event: DeliveryEventFacts): DeliveryOutcome {
  const credit = creditEffectForEvent(event.type);

  switch (event.type) {
    case 'send':
      // Reconciles slice 5's `unknown`: the provider accepted it, and this is
      // the event that says so with the provider's own id attached.
      return { credit, suppression: null, messageStatus: 'sent', recipientStatus: 'sent' };

    case 'delivery':
      return {
        credit,
        suppression: null,
        messageStatus: 'delivered',
        recipientStatus: 'delivered',
      };

    case 'bounce':
      return {
        credit,
        // The compliance spine, and the only branch that can permanently stop
        // mailing somebody. `hard_bounce` exactly when SES said `Permanent`.
        suppression: isHardBounce(event.bounce?.type ?? null) ? 'hard_bounce' : null,
        messageStatus: 'bounced',
        recipientStatus: 'bounced',
      };

    case 'complaint':
      return {
        credit,
        // `not-spam` is a withdrawal, not a complaint. Everything else — an
        // explicit type or none at all — suppresses.
        suppression: event.complaint?.feedbackType === 'not_spam' ? null : 'complaint',
        // Deliberately null. The message was delivered and stays delivered.
        messageStatus: null,
        recipientStatus: null,
      };

    case 'reject':
    case 'rendering_failure':
      // SES refused the message. Nothing was delivered and nothing is wrong with
      // the address, so the reservation unwinds and nobody is suppressed.
      return { credit, suppression: null, messageStatus: 'failed', recipientStatus: 'failed' };

    case 'delivery_delay':
      // Still in flight. Recorded in `delivery_events` so a person can see the
      // message is late, and touching nothing else.
      return { credit, suppression: null, messageStatus: null, recipientStatus: null };

    case 'subscription':
    case 'open':
    case 'click':
      return { credit, suppression: null, messageStatus: null, recipientStatus: null };
  }
}

/**
 * A message that will never move again.
 *
 * `unknown` is **not** terminal: it is slice 5's "provider accepted, we did not
 * record it", and the whole point of slice 6 is that an event resolves it.
 */
export function isTerminalMessageStatus(status: MessageStatus): boolean {
  return status === 'delivered' || status === 'bounced' || status === 'failed';
}

/**
 * Whether an event's credit movement may still be applied, given what we already
 * recorded about the message.
 *
 * **SNS does not guarantee ordering.** A `Delivery` published before a `Bounce`
 * can arrive after it, and applying the consume then would take a reservation
 * the bounce has already released — `credit_balances.reserved >= 0` would refuse
 * it and abort the whole ingest transaction, so an out-of-order event would look
 * like an outage rather than like the no-op it should be.
 *
 * The idempotency key stops the *same* movement twice; this stops a *different*
 * movement after the message is settled. They are different guards and both are
 * needed.
 */
export function isCreditEffectApplicable(current: MessageStatus, effect: CreditEffect): boolean {
  if (effect === null) return false;
  return !isTerminalMessageStatus(current);
}

/* -------------------------------------------------------------------------- */
/* SNS transport — verify first, parse second, act third                       */
/* -------------------------------------------------------------------------- */

/**
 * SNS's `Type` field. Kept in AWS's own spelling, unlike
 * {@link SES_EVENT_TYPE_VALUES}, and the difference is not an oversight: this
 * value is consumed by one branch in the endpoint and is never written to a
 * column, so it has the lifetime of a request rather than of the database.
 */
export const SNS_MESSAGE_TYPE_VALUES = [
  'Notification',
  'SubscriptionConfirmation',
  'UnsubscribeConfirmation',
] as const;
export const snsMessageTypeSchema = z.enum(SNS_MESSAGE_TYPE_VALUES);
export type SnsMessageType = z.infer<typeof snsMessageTypeSchema>;

/**
 * Hostname of an AWS SNS endpoint: `sns.<region>.amazonaws.com`, plus the
 * `.com.cn` partition.
 *
 * **Anchored on purpose.** The bug this shape exists to prevent is
 * `hostname.endsWith('amazonaws.com')`, which happily accepts
 * `sns.evil-amazonaws.com` and `amazonaws.com.attacker.net`. Anchoring both ends
 * also refuses `sns.us-east-1.amazonaws.com.` — a trailing dot, which DNS
 * resolves identically and a naive suffix check treats as a different host.
 */
const SNS_HOST_PATTERN = /^sns\.[a-z0-9-]+\.amazonaws\.com(\.cn)?$/;

/**
 * The five fields of a parsed URL this guard reads, and the constructor that
 * produces them.
 *
 * Declared here rather than by adding `DOM` to this package's `lib`. That would
 * put `document`, `window` and `fetch` in scope for every contract in
 * `@wend/shared` — a package the API imports — and the first accidental use of
 * one is a runtime crash on the server rather than a type error. `Intl` needed
 * none of this because it is in the ES2022 library; `URL` is a host object, and
 * naming the five properties we depend on is cheaper than importing a browser.
 *
 * **Not hand-rolled with a regex on purpose.** Splitting a URL by hand is how
 * `https://sns.us-east-1.amazonaws.com@attacker.test/x.pem` gets read as an AWS
 * host. The platform parser resolves userinfo, punycode, case and `..` segments
 * the way the HTTP client that will make the request does, which is the only
 * parser whose answer is worth having.
 */
type ParsedUrl = {
  readonly protocol: string;
  readonly username: string;
  readonly password: string;
  readonly hostname: string;
  readonly port: string;
  readonly pathname: string;
};

const UrlConstructor = (globalThis as { URL?: new (input: string) => ParsedUrl }).URL;

/**
 * Is this a URL we are willing to make a request to?
 *
 * **Fails closed at every step**, which is the opposite of `isKnownTimeZone` in
 * `campaign.ts` and is deliberate: that one answers a formatting question, this
 * one decides whether an unauthenticated stranger gets to choose a URL our
 * server will fetch. A runtime without `URL` gets `false`, not the benefit of
 * the doubt.
 *
 * Each check earns its place:
 *   - **https only** — a plaintext fetch of a signing certificate is a
 *     certificate an on-path attacker chooses;
 *   - **no credentials in the URL** — `https://sns.x.amazonaws.com@evil.test/`
 *     has hostname `evil.test`, which the pattern already refuses, but userinfo
 *     also leaks into logs and proxy traces, so it is refused outright;
 *   - **no explicit port** — AWS serves this on 443. A port is either pointless
 *     or is somebody reaching for an internal service;
 *   - **hostname matches, anchored** — see {@link SNS_HOST_PATTERN};
 *   - **`.pem` path**, for the certificate only. `URL` normalises `..` segments
 *     before we see them.
 */
function isAwsSnsUrl(raw: string | null | undefined): ParsedUrl | null {
  if (!raw || UrlConstructor === undefined) return null;
  let url: ParsedUrl;
  try {
    url = new UrlConstructor(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  if (url.username !== '' || url.password !== '') return null;
  if (url.port !== '') return null;
  if (!SNS_HOST_PATTERN.test(url.hostname)) return null;
  return url;
}

/**
 * **The security-critical line in this slice.** True only for a URL the
 * signature verifier may fetch a certificate from.
 *
 * SLICES: *the certificate URL must be checked to be an `amazonaws.com` HTTPS
 * host **before** any request is made, or the verifier becomes an SSRF.* The
 * order matters more than the check: a verifier that fetches first and validates
 * afterwards has already made the request that was the attack.
 *
 * A forged event that got past this could consume or release another tenant's
 * credits and permanently suppress an address, which is why it is one pure
 * function of one string with no I/O — testable exhaustively, from a script,
 * with no AWS account.
 */
export function isTrustedSnsSigningCertUrl(raw: string | null | undefined): boolean {
  const url = isAwsSnsUrl(raw);
  return url !== null && url.pathname.endsWith('.pem');
}

/**
 * The same host rule for `SubscribeURL`.
 *
 * Confirming a subscription means making a GET to a URL that arrived in the
 * request body, so it is the same SSRF in a different field and is refused the
 * same way. The host check is necessary and not sufficient: the endpoint must
 * also confirm only for the topic ARN it expects, which is config this package
 * cannot see.
 */
export function isTrustedSnsSubscribeUrl(raw: string | null | undefined): boolean {
  return isAwsSnsUrl(raw) !== null;
}

/**
 * What the signature check concluded. Written to `webhook_events` beside the raw
 * body, before anything is understood.
 *
 * SLICES: *the raw payload is stored before it is understood* — an event we
 * could not parse must still exist, because the alternative is a delivery that
 * silently never happened.
 */
export const SIGNATURE_VERDICT_VALUES = [
  'valid',
  'invalid_signature',
  /** The URL failed {@link isTrustedSnsSigningCertUrl}. **Nothing was fetched.** */
  'untrusted_certificate_url',
  'unsupported_signature_version',
  'certificate_unavailable',
  /** The body was not an SNS envelope at all. */
  'malformed',
] as const;
export const signatureVerdictSchema = z.enum(SIGNATURE_VERDICT_VALUES);
export type SignatureVerdict = z.infer<typeof signatureVerdictSchema>;

/**
 * **The only verdict that may reach the ledger.**
 *
 * A function rather than a comparison at the call site, because the failure mode
 * is a caller writing `verdict !== 'invalid_signature'` — which is true for
 * every one of the other four failures, and which would let an event whose
 * certificate URL pointed at an attacker's host consume a tenant's credits.
 * Criterion 3 is exactly this: *a forged event is rejected and never reaches the
 * ledger.*
 */
export function mayActOnEvent(verdict: SignatureVerdict): boolean {
  return verdict === 'valid';
}

/**
 * The SNS envelope, parsed leniently.
 *
 * `.passthrough()` because AWS adds fields and refusing a surplus one would
 * refuse a real notification. Every field is optional for the same reason: this
 * schema's job is to get us to a signature check, not to decide whether the
 * payload is genuine — that is what the signature is for, and a shape that
 * refused before verification would be a second, weaker gate in front of the
 * real one.
 *
 * **`Message` is a JSON string, not an object.** The SES event is double-encoded
 * inside the envelope, and that is where a first implementation reaches for
 * `body.Message.eventType` and gets `undefined` on every event forever.
 * {@link sesEventNotificationSchema} parses the result of `JSON.parse` on it.
 *
 * `SigningCertURL` is AWS's documented spelling; `SigningCertUrl` has appeared
 * in their own examples and SDKs. Both are accepted and
 * {@link signingCertUrlOf} is the only supported way to read it, so no caller
 * picks one spelling and quietly never validates the other.
 */
export const snsEnvelopeSchema = z
  .object({
    Type: z.string().optional(),
    MessageId: z.string().optional(),
    TopicArn: z.string().optional(),
    /** Double-encoded JSON. See above. */
    Message: z.string().optional(),
    Timestamp: z.string().optional(),
    SignatureVersion: z.string().optional(),
    Signature: z.string().optional(),
    SigningCertURL: z.string().optional(),
    SigningCertUrl: z.string().optional(),
    /** SubscriptionConfirmation only. Validated by {@link isTrustedSnsSubscribeUrl}. */
    SubscribeURL: z.string().optional(),
    SubscribeUrl: z.string().optional(),
    Token: z.string().optional(),
  })
  .passthrough();
export type SnsEnvelope = z.infer<typeof snsEnvelopeSchema>;

/** The signing certificate URL, whichever spelling arrived. Never both readers. */
export function signingCertUrlOf(envelope: SnsEnvelope): string | null {
  return envelope.SigningCertURL ?? envelope.SigningCertUrl ?? null;
}

/** The subscription-confirmation URL, whichever spelling arrived. */
export function subscribeUrlOf(envelope: SnsEnvelope): string | null {
  return envelope.SubscribeURL ?? envelope.SubscribeUrl ?? null;
}

/* -------------------------------------------------------------------------- */
/* The SES event body                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The SES event, parsed out of the envelope's `Message`.
 *
 * Lenient throughout, and passthrough, for one reason: **this payload is already
 * stored.** Anything this schema declines to understand survives in
 * `webhook_events.payload`, so being strict here would buy nothing and would
 * turn a new AWS field into a rejected delivery.
 *
 * `eventType` is the only field parsed into our own vocabulary here; the bounce
 * and complaint blocks are kept in AWS's spelling and translated by
 * {@link sesEventFacts}, so the raw shape and the decided shape are visibly two
 * different things.
 */
export const sesEventNotificationSchema = z
  .object({
    eventType: z.string().optional(),
    /** Older SES notifications published to SNS use `notificationType`. */
    notificationType: z.string().optional(),
    mail: z
      .object({
        /** SES's id for the message — `messages.provider_message_id`. */
        messageId: z.string().optional(),
        timestamp: z.string().optional(),
        destination: z.array(z.string()).optional(),
        tags: z.record(z.string(), z.array(z.string())).optional(),
      })
      .passthrough()
      .optional(),
    bounce: z
      .object({
        bounceType: z.string().optional(),
        bounceSubType: z.string().optional(),
        timestamp: z.string().optional(),
        feedbackId: z.string().optional(),
        bouncedRecipients: z
          .array(
            z
              .object({
                emailAddress: z.string().optional(),
                /** The remote server's own words. Useful; also very leaky — see below. */
                diagnosticCode: z.string().optional(),
                status: z.string().optional(),
              })
              .passthrough(),
          )
          .optional(),
      })
      .passthrough()
      .optional(),
    complaint: z
      .object({
        complaintFeedbackType: z.string().optional(),
        timestamp: z.string().optional(),
        feedbackId: z.string().optional(),
      })
      .passthrough()
      .optional(),
    delivery: z.object({ timestamp: z.string().optional() }).passthrough().optional(),
    deliveryDelay: z
      .object({ delayType: z.string().optional(), expirationTime: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type SesEventNotification = z.infer<typeof sesEventNotificationSchema>;

/**
 * Turns a parsed SES notification into the facts {@link deliveryOutcome} reads.
 *
 * Returns `null` when the event type is not one we recognise — the caller stores
 * the payload, leaves `processed_at` null and acts on nothing. That is the
 * behaviour a new AWS event type gets, and it is why an unrecognised event is a
 * row rather than an exception.
 *
 * `eventType` is preferred over `notificationType`: a configuration-set event
 * destination sets the first, and SES's older identity-level SNS notifications
 * set the second. Both reach the same endpoint if a tenant has both configured,
 * and reading only one of them loses half the events on an account somebody
 * migrated.
 */
export function sesEventFacts(notification: SesEventNotification): DeliveryEventFacts | null {
  const type = parseSesEventType(notification.eventType ?? notification.notificationType);
  if (type === null) return null;
  return {
    type,
    bounce: notification.bounce
      ? {
          type: parseSesBounceType(notification.bounce.bounceType),
          subType: parseSesBounceSubType(notification.bounce.bounceSubType),
        }
      : null,
    complaint: notification.complaint
      ? { feedbackType: parseSesComplaintFeedbackType(notification.complaint.complaintFeedbackType) }
      : null,
  };
}

/* -------------------------------------------------------------------------- */
/* The stored event                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One row of `delivery_events`, as a screen reads it.
 *
 * `provider` reuses {@link integrationKindSchema} rather than declaring a third
 * `'ses' | 'meta_whatsapp'`: it is the same vocabulary, an integration is where
 * the events come from, and CONTRACT.md's unique constraint is
 * `(provider, provider_event_id)`.
 *
 * **`providerEventId` is the SNS `MessageId`**, not SES's `feedbackId`. The
 * feedback id only exists on bounces and complaints, and a de-duplication scheme
 * that works for two of ten event types is a scheme with a hole in it. The SNS
 * message id is stable across SNS's own retries, which is exactly the redelivery
 * this constraint exists to absorb.
 *
 * It is worth being clear about which idempotency guard does what, because they
 * are easy to conflate and only one of them is load-bearing for money:
 *
 *   - `(provider, provider_event_id)` keeps the **event log** honest — one row
 *     per thing AWS told us, however many times it told us;
 *   - `credit_ledger.idempotency_key` keeps the **ledger** honest — one movement
 *     per message per kind, however many distinct events say the same thing.
 *
 * The second is why criterion 2 holds even if AWS publishes two genuinely
 * different SNS messages carrying the same delivery.
 */
export const deliveryEventSchema = z
  .object({
    id: uuidSchema,
    provider: integrationKindSchema,
    /** The SNS message id. Unique per `(provider, provider_event_id)`. */
    providerEventId: z.string(),
    type: sesEventTypeSchema,
    messageId: uuidSchema.nullable(),
    campaignId: uuidSchema.nullable(),
    bounceType: sesBounceTypeSchema.nullable(),
    bounceSubType: sesBounceSubTypeSchema.nullable(),
    complaintFeedbackType: sesComplaintFeedbackTypeSchema.nullable(),
    /**
     * The remote mail server's own explanation, when there was one.
     *
     * Shown to the tenant — it is their recipient and their bounce, and "550
     * 5.1.1 user unknown" is the difference between a mystery and a repair. It
     * must not reach a log line: it routinely contains the full recipient
     * address, which slice 0's masking convention exists to keep out of logs.
     */
    diagnostic: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
    receivedAt: isoDateTimeSchema,
  })
  .strict();
export type DeliveryEvent = z.infer<typeof deliveryEventSchema>;

export const deliveryEventListQuerySchema = paginationQuerySchema.extend({
  type: sesEventTypeSchema.optional(),
  campaignId: uuidSchema.optional(),
  messageId: uuidSchema.optional(),
});
export type DeliveryEventListQuery = z.infer<typeof deliveryEventListQuerySchema>;

export const deliveryEventListResponseSchema = paginated(deliveryEventSchema);
export type DeliveryEventListResponse = z.infer<typeof deliveryEventListResponseSchema>;

/**
 * What the public webhook answers.
 *
 * Deliberately says **nothing about whether the event matched a message.** The
 * endpoint is unauthenticated by construction, and a response that distinguished
 * "matched" from "did not match" would be an oracle an attacker could walk
 * through message ids with. `verdict` is safe: it tells the sender only what it
 * could work out from being ignored.
 */
export const deliveryIngestResultSchema = z
  .object({
    received: z.literal(true),
    verdict: signatureVerdictSchema,
    /** True when the raw payload was stored. It is stored before it is understood. */
    stored: z.boolean(),
    /** True when the event was recognised and applied. Never says to what. */
    processed: z.boolean(),
  })
  .strict();
export type DeliveryIngestResult = z.infer<typeof deliveryIngestResultSchema>;

/* -------------------------------------------------------------------------- */
/* Campaign results — counts by delivery state, derived on read                */
/* -------------------------------------------------------------------------- */

/**
 * Messages by status.
 *
 * The same explicit-object shape as `recipientStatusCounts`, and for the same
 * reason: a `GROUP BY` returns no row for a status nothing has, and building the
 * object from the rows alone renders a blank where a zero belongs.
 *
 * There is no `complained` member, here or in `MessageStatus`. A complaint
 * happens *after* a delivery, so a message that was delivered and complained
 * about is `delivered`; a `complained` status would overwrite that, and with it
 * the record of why that message's credit stays consumed. Complaints are their
 * own count on {@link campaignResultsSchema}.
 */
export const messageStatusCountsSchema = z
  .object({
    queued: z.number().int().min(0),
    sent: z.number().int().min(0),
    delivered: z.number().int().min(0),
    bounced: z.number().int().min(0),
    failed: z.number().int().min(0),
    /** Provider accepted, we never recorded it. Resolved by a `Send` event. */
    unknown: z.number().int().min(0),
  })
  .strict();
export type MessageStatusCounts = z.infer<typeof messageStatusCountsSchema>;

export const EMPTY_MESSAGE_STATUS_COUNTS: MessageStatusCounts = {
  queued: 0,
  sent: 0,
  delivered: 0,
  bounced: 0,
  failed: 0,
  unknown: 0,
};

/** Builds the counts from a `GROUP BY status`. Zeros first — see above. */
export function messageStatusCounts(
  rows: readonly { readonly status: string; readonly count: number }[],
): MessageStatusCounts {
  const counts: MessageStatusCounts = { ...EMPTY_MESSAGE_STATUS_COUNTS };
  for (const row of rows) {
    const status = messageStatusSchema.safeParse(row.status);
    if (status.success) counts[status.data] = row.count;
  }
  return counts;
}

/**
 * Bounces split the way the product acts on them.
 *
 * `hard` is the number of addresses that will never accept and have been
 * suppressed; `soft` is the number that might work tomorrow. Presenting one
 * "bounced" figure is what makes a customer delete a list they should have
 * retried, or retry a list they should have cleaned.
 */
export const bounceBreakdownSchema = z
  .object({
    hard: z.number().int().min(0),
    soft: z.number().int().min(0),
    /** SES could not classify it. Suppresses nobody — see {@link isHardBounce}. */
    undetermined: z.number().int().min(0),
  })
  .strict();
export type BounceBreakdown = z.infer<typeof bounceBreakdownSchema>;

export const EMPTY_BOUNCE_BREAKDOWN: BounceBreakdown = { hard: 0, soft: 0, undetermined: 0 };

/** Builds the breakdown from a `GROUP BY bounce_type` over `delivery_events`. */
export function bounceBreakdown(
  rows: readonly { readonly bounceType: string | null; readonly count: number }[],
): BounceBreakdown {
  const counts: BounceBreakdown = { ...EMPTY_BOUNCE_BREAKDOWN };
  for (const row of rows) {
    const parsed = row.bounceType === null ? null : parseSesBounceType(row.bounceType);
    // A bounce whose type SES did not give us is counted with the undetermined
    // ones rather than dropped: it happened, and the totals have to add up to
    // the `bounced` message count or the screen contradicts itself.
    if (parsed === 'permanent') counts.hard = row.count;
    else if (parsed === 'transient') counts.soft = row.count;
    else counts.undetermined += row.count;
  }
  return counts;
}

/* -------------------------------------------------------------------------- */
/* Not delivered — five outcomes that stay five                                */
/* -------------------------------------------------------------------------- */

/**
 * The ways a message the provider took can end up not arriving, kept apart.
 *
 * **These are never summed into a single "not delivered" figure, and this
 * schema has no `total` member for that reason.** Adding one is not a
 * convenience, it is the bug: the five differ in who has to do something and
 * what that something is, and one number tells a person only that something is
 * wrong somewhere.
 *
 *   - `rejected` — SES refused it before it left. Content or a suppressed
 *     address on AWS's own list. Nothing is in flight and nothing will retry.
 *   - `hardBounced` — `bounce` + `Permanent`. The address is dead. It has
 *     already been suppressed by {@link deliveryOutcome} and the repair is to
 *     the list, once.
 *   - `softBounced` — `bounce` + `Transient`. Mailbox full, greylisted,
 *     throttled. The address is probably fine and the campaign can be sent
 *     again later. Cleaning these off a list is the expensive mistake.
 *   - `delayed` — `delivery_delay`. SES is **still trying**. This is not a
 *     failure yet, and a screen that files it under failures makes a tenant
 *     act on a message that may well arrive.
 *   - `renderingFailed` — `rendering_failure`. Our template broke on a
 *     variable. The only one of the five that is Wend's fault and the only one
 *     the tenant fixes by editing something inside this product.
 *
 * A bounce SES would not classify is **not** here. It is neither hard nor soft,
 * and inventing a sixth member for it would be a category the product cannot
 * act on; it stays visible as {@link bounceBreakdownSchema}'s `undetermined`,
 * which every screen showing this object also shows.
 *
 * Lives in this file rather than in `./overview.js`, where slice 10 assembles
 * it, because the five members *are* this file's vocabulary — `reject`,
 * `bounce`/`Permanent`, `bounce`/`Transient`, `delivery_delay`,
 * `rendering_failure` are {@link SES_EVENT_TYPE_VALUES} and
 * {@link SES_BOUNCE_TYPE_VALUES} and nothing else. The overview imports it. The
 * reverse — `delivery.ts` importing the overview so that
 * {@link campaignResultsSchema} could carry it — would make the two files a
 * cycle, and a cycle between modules that both build zod schemas at load time
 * fails at import rather than at review.
 */
export const notDeliveredBreakdownSchema = z
  .object({
    /** SES refused before sending. */
    rejected: z.number().int().min(0),
    /** `bounce` + `Permanent`. Dead address; already suppressed. */
    hardBounced: z.number().int().min(0),
    /** `bounce` + `Transient`. Retryable; do not clean these off the list. */
    softBounced: z.number().int().min(0),
    /** `delivery_delay`. Still being attempted. Not a failure. */
    delayed: z.number().int().min(0),
    /** `rendering_failure`. A template variable broke. Ours to fix. */
    renderingFailed: z.number().int().min(0),
  })
  .strict();
export type NotDeliveredBreakdown = z.infer<typeof notDeliveredBreakdownSchema>;

export const EMPTY_NOT_DELIVERED_BREAKDOWN: NotDeliveredBreakdown = {
  rejected: 0,
  hardBounced: 0,
  softBounced: 0,
  delayed: 0,
  renderingFailed: 0,
};

/**
 * Builds the breakdown from a `GROUP BY event_type, bounce_type` over
 * `delivery_events`.
 *
 * One builder so the four call sites that will want this — campaign results,
 * the overview, and whatever slice 11 and 12 add — cannot each decide for
 * themselves whether an undetermined bounce is soft. It is not; see the note on
 * {@link notDeliveredBreakdownSchema}. Rows the mapping does not recognise are
 * ignored rather than bucketed, because a `send` or `delivery` row arriving
 * here means the caller's query was wrong and quietly adding it to a failure
 * count would hide that.
 */
export function notDeliveredBreakdown(
  rows: readonly {
    readonly eventType: string;
    readonly bounceType?: string | null;
    readonly count: number;
  }[],
): NotDeliveredBreakdown {
  const counts: NotDeliveredBreakdown = { ...EMPTY_NOT_DELIVERED_BREAKDOWN };
  for (const row of rows) {
    const type = parseSesEventType(row.eventType);
    if (type === 'reject') counts.rejected += row.count;
    else if (type === 'delivery_delay') counts.delayed += row.count;
    else if (type === 'rendering_failure') counts.renderingFailed += row.count;
    else if (type === 'bounce') {
      const bounce = row.bounceType == null ? null : parseSesBounceType(row.bounceType);
      if (bounce === 'permanent') counts.hardBounced += row.count;
      else if (bounce === 'transient') counts.softBounced += row.count;
      // `undetermined` deliberately falls through to nothing.
    }
  }
  return counts;
}

/**
 * **What the results screen reads.** Computed on read, never stored.
 *
 * The segment-counts precedent, and the argument is at its strongest here: a
 * stored `delivered_count` is a second source of truth maintained by the webhook
 * handler, so it drifts on precisely the events slice 6 is built around — a
 * replayed notification, an out-of-order pair, a reprocessed
 * `webhook_events` row. The ledger and `campaign_recipients` already know, and
 * both are protected by constraints that no counter can have.
 *
 * **`recipients` and `messages` will not add up to the same total, and that is
 * correct.** A skipped recipient never became a message, and an `unknown`
 * message has no settled recipient status. The two answer different questions —
 * *what happened to each person* and *what happened to each message* — and a
 * screen that presents either as the other is the "accepted is not delivered"
 * mistake wearing different clothes.
 */
export const campaignResultsSchema = z
  .object({
    campaignId: uuidSchema,
    /** What happened to each person. The shape slice 5's progress poll returns. */
    recipients: recipientStatusCountsSchema,
    /** What happened to each message. Includes `unknown`, which has no recipient state. */
    messages: messageStatusCountsSchema,
    bounces: bounceBreakdownSchema,
    /**
     * Why the messages that did not arrive did not arrive, kept as five
     * separate outcomes. Never summed — see {@link notDeliveredBreakdownSchema}.
     *
     * It overlaps {@link CampaignResults.bounces} on purpose: `bounces` splits
     * the bounced *messages*, this splits the *events* that stopped delivery,
     * and the two disagree in exactly the informative case — a rejection or a
     * rendering failure never became a bounce and so appears in only one of
     * them.
     */
    notDelivered: notDeliveredBreakdownSchema,
    /** Complaints. Not a message status — see {@link messageStatusCountsSchema}. */
    complaints: z.number().int().min(0),
    /** Straight from the ledger. Never a projection — SLICES: counted, never estimated. */
    credits: campaignCreditTotalsSchema,
    /** The most recent provider event. Null until one arrives. */
    lastEventAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type CampaignResults = z.infer<typeof campaignResultsSchema>;

/**
 * What `GET /campaigns/:id/results` answers.
 *
 * `.strict()` since slice 28, and it is doing the same work here that it does on
 * the Overview: the projection added below is a figure nobody measured, and the
 * property that makes it safe is that a field this response did not declare
 * fails at the parse line rather than on a screen. A non-strict object would
 * have let a later mapper add one silently.
 */
export const campaignResultsResponseSchema = z
  .object({
    results: campaignResultsSchema,
    /**
     * Projected delivery, opens and clicks over this campaign's unreported
     * messages — `null` whenever any report has ever reached Wend, whenever
     * anything accepted has a terminal outcome, and always on WhatsApp.
     *
     * A sibling of `results`, never a member of it. `campaignResultsSchema`
     * carries counts and credits straight from the ledger, and its own docs say
     * *counted, never estimated*; putting a projection inside it would put an
     * assumption in the same object as the figures that rule protects. See
     * `./projection.js` for what holds it away from the halt gate.
     */
    projection: projectedFiguresSchema.nullable().default(null),
  })
  .strict();
export type CampaignResultsResponse = z.infer<typeof campaignResultsResponseSchema>;

/**
 * AWS's published account-level thresholds, as fractions.
 *
 * Carried so a tenant can see trouble coming in their own campaign rather than
 * in an AWS email — under BYO a suspension lands on their account, and the first
 * warning should not be from Amazon.
 *
 * **These are approximations of AWS's number, not AWS's number.** AWS computes
 * reputation over the whole account across a rolling window; this is one
 * campaign. It is an early warning, and a screen that presents it as the figure
 * in the SES console is making a promise Wend cannot keep.
 */
export const AWS_BOUNCE_RATE_REVIEW = 0.05;
export const AWS_BOUNCE_RATE_PAUSE = 0.1;
export const AWS_COMPLAINT_RATE_REVIEW = 0.001;
export const AWS_COMPLAINT_RATE_PAUSE = 0.005;

/**
 * Below this many accepted messages, a rate is noise and is not shown as a
 * warning.
 *
 * One bounce out of eight is a 12.5% bounce rate and means nothing. A product
 * that shouts about it teaches people to ignore the warning that is real — the
 * same argument quiet hours make in slice 5. In a guarded development
 * environment nearly every campaign sits below this, and that is the correct
 * outcome rather than a gap in coverage.
 */
export const RATE_SIGNIFICANCE_MINIMUM = 100;

/** The derived numbers a results screen prints. Never stored, never returned raw. */
export interface CampaignResultsReading {
  /**
   * Messages the provider took: `sent + delivered + bounced`.
   *
   * The denominator of every rate below. `failed` is excluded because the
   * provider never took it, and `unknown` is excluded because we cannot say
   * whether it did — see {@link CampaignResultsReading.unresolved}.
   */
  readonly accepted: number;
  /** Accepted, with no terminal event yet. The number that has to reach zero. */
  readonly awaitingEvents: number;
  /** Accepted-but-unrecorded messages, deliberately outside every rate. */
  readonly unresolved: number;
  /** 0–1. `delivered / accepted`. */
  readonly deliveryRate: number;
  /** 0–1, hard and soft together. */
  readonly bounceRate: number;
  /** 0–1, hard only — the half that suppresses and the half AWS reacts to. */
  readonly hardBounceRate: number;
  /** 0–1. */
  readonly complaintRate: number;
  /** False below {@link RATE_SIGNIFICANCE_MINIMUM}; the rates are still computed. */
  readonly ratesAreSignificant: boolean;
  /** Significant **and** at or over AWS's review threshold. */
  readonly bounceRateConcerning: boolean;
  readonly complaintRateConcerning: boolean;
  /**
   * Reservations this campaign still holds: `reserved − consumed − released`.
   *
   * In a healthy campaign it equals {@link CampaignResultsReading.awaitingEvents}
   * — one held reservation per message still waiting for its event. Both are on
   * this object so a disagreement is visible on the screen instead of being
   * discovered by the reconciliation job, which runs hourly — see
   * RECONCILE_INTERVAL_MS in credit-reconciliation.scheduler.ts. Do not restate
   * the interval here: it is the scheduler s to retune, and a second copy of it
   * in a contract is a second place for it to go stale. This comment said
   * nightly until slice 17, when the screen that repeated it was corrected.
   */
  readonly creditsStillReserved: number;
  /** True when the two above agree. A false here is the drift alarm, early. */
  readonly creditsBalanceWithMessages: boolean;
}

export function campaignResultsReading(results: CampaignResults): CampaignResultsReading {
  const m = results.messages;
  const accepted = m.sent + m.delivered + m.bounced;
  const rate = (numerator: number): number => (accepted === 0 ? 0 : numerator / accepted);

  const bounceRate = rate(m.bounced);
  const hardBounceRate = rate(results.bounces.hard);
  const complaintRate = rate(results.complaints);
  const significant = accepted >= RATE_SIGNIFICANCE_MINIMUM;
  const stillReserved = creditsStillReserved(results.credits);

  return {
    accepted,
    awaitingEvents: m.sent,
    unresolved: m.unknown,
    deliveryRate: rate(m.delivered),
    bounceRate,
    hardBounceRate,
    complaintRate,
    ratesAreSignificant: significant,
    bounceRateConcerning: significant && bounceRate >= AWS_BOUNCE_RATE_REVIEW,
    complaintRateConcerning: significant && complaintRate >= AWS_COMPLAINT_RATE_REVIEW,
    creditsStillReserved: stillReserved,
    creditsBalanceWithMessages: stillReserved === m.sent,
  };
}

/**
 * Zero results for a campaign nothing has happened to yet.
 *
 * Exists so a screen renders zeros rather than a spinner for a campaign that has
 * been scheduled and not yet sent — the `materialisedAt` lesson from slice 5,
 * one screen along.
 */
export function emptyCampaignResults(
  campaignId: string,
  recipients: RecipientStatusCounts,
  credits: CampaignCreditTotals,
): CampaignResults {
  return {
    campaignId,
    recipients,
    messages: { ...EMPTY_MESSAGE_STATUS_COUNTS },
    bounces: { ...EMPTY_BOUNCE_BREAKDOWN },
    notDelivered: { ...EMPTY_NOT_DELIVERED_BREAKDOWN },
    complaints: 0,
    credits,
    lastEventAt: null,
  };
}

/**
 * The suppression a delivery event caused, as the suppression list will show it.
 *
 * Re-exported through this shape rather than left implicit so that the two
 * reasons slice 6 introduces — `hard_bounce` and `complaint` — are visibly the
 * ones {@link deliveryOutcome} can produce, and the other two
 * (`unsubscribe`, `manual`) visibly are not.
 */
export const EVENT_DRIVEN_SUPPRESSION_REASONS = [
  'hard_bounce',
  'complaint',
] as const satisfies readonly SuppressionReason[];
